﻿import { useCallback, useMemo, useState } from 'react'
import {
  DEFAULT_TICKET_FILTERS,
  countActiveFilters,
  filterTickets,
} from '../utils/ticketTable'
import { employeeName } from '../utils/employee'

/** Ordena empleados por nombre; los que no están en el catálogo quedan con su id. */
const toSortedPeople = (ids, byId) =>
  [...new Set(ids.filter(Boolean))]
    .map((id) => ({ ...(byId[id] || {}), value: id }))
    .sort((a, b) => employeeName(a).localeCompare(employeeName(b)))

/**
 * Criterios de búsqueda sobre una lista de tickets ya cargada (tabla y
 * tablero Kanban): estado de los filtros, lista filtrada y las opciones de
 * los selectores derivadas de los propios tickets.
 *
 * @param {Array<Object>} items - Tickets a filtrar.
 * @param {Object} catalogs - Empleados indexados (de `useTicketCatalogs`).
 * @param {Record<string, Object>} catalogs.employeeById
 * @param {Record<string, Object>} catalogs.employeeByAuthId
 * @param {Array<Object>} [catalogs.statuses] - Estados (para el filtro de estado).
 * @param {Array<Object>} [catalogs.queues] - Colas (para el filtro de cola).
 * @returns {{
 *   filters: Object,
 *   setFilter: (key: string, value: string) => void,
 *   clearFilters: () => void,
 *   activeCount: number,
 *   filtered: Array<Object>,
 *   options: {origins: string[], responsibles: Array<Object>, authors: Array<Object>}
 * }}
 */
export default function useTicketFilters(
  items,
  { employeeById = {}, employeeByAuthId = {}, statuses = [], queues = [] } = {}
) {
  const [filters, setFilters] = useState(DEFAULT_TICKET_FILTERS)

  const setFilter = useCallback(
    (key, value) => setFilters((prev) => ({ ...prev, [key]: value })),
    []
  )
  const clearFilters = useCallback(() => setFilters(DEFAULT_TICKET_FILTERS), [])

  const filtered = useMemo(
    () => filterTickets(items, filters, { employeeById, employeeByAuthId }),
    [items, filters, employeeById, employeeByAuthId]
  )

  // Sólo se ofrecen valores que existen en la lista cargada.
  const options = useMemo(
    () => ({
      origins: [
        ...new Set(items.map((i) => i.systemOrigin).filter(Boolean)),
      ].sort(),
      responsibles: toSortedPeople(
        items.map((i) => i.responsibleId),
        employeeById
      ),
      authors: toSortedPeople(
        items.map((i) => i.userId),
        employeeByAuthId
      ),
      statuses,
      queues,
    }),
    [items, employeeById, employeeByAuthId, statuses, queues]
  )

  return {
    filters,
    setFilter,
    clearFilters,
    activeCount: countActiveFilters(filters),
    filtered,
    options,
  }
}
